<template>
  <div class="flex flex-col">
    <!-- Breadcrumb -->
    <div class="flex mt-10 mb-4">
      <div
        v-for="(item, index) in breadcrumb"
        :key="index"
        :class="[
          'breadcrumb-item',
          !isSubmitted && 'breadcrumb-item--clickable',
          index == breadcrumb.length - 1 && 'breadcrumb-item--active'
        ]"
        @click="!isSubmitted && backAt(index)"
      >
        <div class="number">{{ index + 1 }}</div>
        <div class="label">{{ item.name }}</div>
      </div>
    </div>

    <!-- Context -->
    <div v-if="isAtRoot && !isSubmitted">
      <div class="heading-big normal-case">Hello.</div>
      <h4
        class="heading-4 normal-case text-base font-semibold leading-none"
      >To get your query answered quickly and by the right department we need to ask you a few questions.</h4>
    </div>
    <h4
      v-if="!isSubmitted"
      class="heading-4 heading-gold normal-case mt-2 text-base font-semibold leading-none"
    >Which category would best describe your question?</h4>

    <!-- Success message -->
    <div v-if="isSubmitted">
      <div class="heading-big normal-case">Thanks.</div>
      <h3
        class="heading-3 leading-h2 normal-case text-xl font-bold"
      >Your question has been submitted to our highly trained team of support staff (They even queue support as a main role). We’ll answer it as soon as we can.</h3>
    </div>

    <!-- Category children -->
    <div v-if="!isSubmitted" class="flex flex-wrap justify-center -ml-4">
      <div v-for="(child, key) in current.children" :key="key" class="w-full lg:w-1/3 pl-4">
        <div
          class="item-card text-shadow"
          :style="`background-image: url('/images/contact/${child.image}');`"
          @click="navigate(key)"
        >
          <h4 class="heading-4 leading-normal text-center text-base font-semibold">{{ child.name }}</h4>
          <div class="text-sm leading-xs text-center">{{ child.description }}</div>
        </div>
      </div>
    </div>

    <!-- Form -->
    <form v-if="isAtLeaf && !isSubmitted" class="flex flex-wrap mt-12" @submit.prevent="submit" method="POST">
      <div class="w-full lg:w-1/2 flex flex-col lg:pr-2">
        <input type="text" v-model="form.name" placeholder="Your Name*" required>
        <input type="email" v-model="form.email" placeholder="Your Email*" required class="mt-3">
        <input type="text" v-model="form.account" placeholder="Account Name" class="mt-3">
        <input
          type="password"
          v-model="form.password"
          placeholder="Account Password"
          class="mt-3"
        >
        <input type="text" v-model="form.region" placeholder="Account Region" class="mt-3">
      </div>
      <div class="w-full mt-3 lg:mt-0 lg:w-1/2 flex lg:pl-2">
        <textarea v-model="form.question" rows="10" placeholder="Your question*" class="flex-1" required></textarea>
      </div>
    </form>

    <div class="mt-8 flex justify-center">
      <a
        v-if="isAtRoot"
        class="btn btn-gold mr-6"
        href="/"
      >
        <span class="btn-content">BACK TO HOME</span>
        <span class="icon">
            <i class="fa fa-arrow-left" aria-hidden="true"></i>
        </span>
      </a>
      <div
        v-if="!isAtRoot && !isSubmitted"
        class="btn btn-white btn-ghost"
        @click="back()"
      >
        <span class="btn-content">BACK</span>
        <span class="icon">
            <i class="fa fa-arrow-left" aria-hidden="true"></i>
        </span>
      </div>
      <div
        v-if="isAtLeaf && !isSubmitted"
        class="btn btn-gold ml-6"
        @click="submit()"
      >
        <span class="btn-content">SUBMIT</span>
        <span class="icon">
            <i class="fas fa-check" aria-hidden="true"></i>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { get, isNil, isEmpty, pick, omit } from 'lodash';

const TREE = {
  root: {
    name: 'HOME',
    children: {
      'pre-purchase': {
        order: 0,
        name: 'PRE-PURCHASE',
        image: 'pre-purchase.png',
        description:
          'Ask a question regarding one of our products, packages or company.',
      },
      'post-purchase': {
        order: 1,
        name: 'POST-PURCHASE',
        image: 'post-purchase.png',
        description:
          'Ask a question or solve a problem with a product you have purchased.',
        children: {
          'smurf-accounts': {
            order: 0,
            name: 'SMURF ACCOUNTS',
            image: 'smurf-accounts.png',
            description:
              'For questions regarding a purchase of an account from our regular store.',
          },
          highends: {
            order: 1,
            name: 'HIGHENDS',
            image: 'highends.png',
            description:
              'For questions regarding a purchase of an account from our high end store.',
          },
          'payment-or-orders': {
            order: 2,
            name: 'PAYMENT / ORDERS',
            image: 'payment-or-orders.png',
            description: 'For problems regarding payment or a previous order.',
          },
          other: {
            order: 3,
            name: 'OTHER',
            image: 'other.png',
            description:
              'Your enquiry doesn’t really fit into any of our other categories.',
          },
        },
      },
      'business-and-billing': {
        order: 2,
        name: 'BUSINESS & BILLING',
        image: 'business-and-billing.png',
        description:
          'For any inquiries involving business, billing. or partnerships.',
      },
    },
  },
};

export default {
  data() {
    return {
      path: ['root'],
      form: {
        name: '',
        email: '',
        account: '',
        password: '',
        region: '',
        question: '',
      },
      isSubmitted: false,
    };
  },

  methods: {
    nodeAtPath(path) {
      const pathInTree = path.join('.children.');
      return get(TREE, pathInTree);
    },

    navigate(key) {
      this.path.push(key);
    },

    back() {
      if (this.path.length <= 1) {
        return;
      }

      this.path.splice(-1, 1);
    },

    backAt(index) {
      this.path.splice(index + 1, this.path.length - index - 1);
    },

    submit() {
      const payload = {
        category: this.path.slice(1).join('.'),
        from_site: 'unranked-smurfs',
        ...this.form,
      };

      const form = document.querySelector('form');

      if(!form.reportValidity()) {
        throw Error('Invalid form inputs')
      }

      axios
        .post('/api/contact/support', payload)
        .then(res => {
          this.isSubmitted = true;
        })
        .catch(err => {
          console.error('Cannot submit contact request');
        });
    },
  },

  computed: {
    isAtRoot() {
      return this.path.length === 1;
    },

    isAtLeaf() {
      return isNil(this.current.children) || isEmpty(this.current.children);
    },

    current() {
      return this.nodeAtPath(this.path);
    },

    breadcrumb() {
      return this.path.reduce((items, key, i) => {
        const node = this.nodeAtPath(this.path.slice(0, i + 1));
        const item = { name: node.name, clickable: !this.isSubmitted };
        return [...items, item];
      }, []);
    },
  },
};
</script>

<style lang="scss" scoped>
.breadcrumb-item {
  @apply flex flex-row;
  @apply border border-almost-black;
  @apply mr-4;

  &--clickable {
    @apply cursor-pointer;
  }

  .number {
    @apply bg-smurfs-gold text-honestly-white px-2;
  }

  .label {
    @apply bg-almost-black text-honestly-white text-sm px-2;
  }

  .number,
  .label {
    @apply border border-almost-black;
  }

  &--active {
    .number {
      @apply bg-almost-black text-smurfs-gold;
    }

    .label {
      @apply bg-smurfs-gold text-almost-black;
    }

    .number,
    .label {
      @apply border border-smurfs-gold;
    }
  }
}

.item-card {
  @apply flex flex-col justify-end items-center;
  @apply border border-almost-black;
  @apply cursor-pointer;
  @apply h-32 mt-4 px-1 py-2;

  background-size: cover;
  background-position: bottom center;
  transition: border 0.2s ease;

  &:hover {
    @apply border-honestly-white;
  }
}

input,
textarea {
  @apply py-3 px-4 bg-almost-black-light border border-almost-black;
  @apply outline-none;
  @apply text-honestly-white;

  &::placeholder {
    color: theme('colors.60');
  }

  &:hover,
  &:focus {
    @apply border-50;
  }
}

input {
  @apply h-10;
}

textarea {
  resize: none;
}

.btn {
  min-width: 9rem;
}
</style>
